import type {
  AggregatedColumnJSON,
  AggregatedTableJSON,
  AggregationRowJSON,
  ApiDashboardElement,
  DataSeriesColumnJSON,
  DataSeriesTableJSON,
  DecimatedSeriesTableJSON,
  Decimation,
  EntityPropertyColumnJSON,
  EntityTypes,
  FreeColumnJSON,
  SelectionSchemaMeasurePoint,
  Table,
  TableConfigured,
  TableJSON,
} from '@highimpact-frontend/api-types';
import { mapEqual } from '../internal/utils/array';
import { isConfiguredTable } from './dashboard-element-operations';

export interface AdditionalColumn<T> {
  insert_index: number;
  name: string;
  value_by_row: Record<string, T>;
}

// -- Builders

export function makeEmptyDataSeriesTable(count = 10): DataSeriesTableJSON {
  return {
    type: 'DataSeriesTable',
    columns: [],
    headLimit: {
      type: 'head',
      count,
    },
    middleLimit: {
      type: 'middle',
      count,
    },
    tailLimit: {
      type: 'tail',
      count,
    },
  };
}

export function makeEmptyDecimatedSeriesTable(
  method: Decimation['method'],
  intervalInSeconds: number
): DecimatedSeriesTableJSON {
  return {
    type: 'DecimatedSeriesTable',
    columns: [],
    decimation: {
      type: 'decimation',
      method,
      intervalInSeconds,
    },
  };
}

export function makeEmptyAggregatedTable(): AggregatedTableJSON {
  return {
    type: 'AggregatedTable',
    rows: [],
  };
}

export function makeAggregationRow(
  columns: (AggregatedColumnJSON | EntityPropertyColumnJSON)[]
): AggregationRowJSON {
  return {
    type: 'AggregationRow',
    columns,
  };
}

export function makeAggregatedColumn(
  transformation_type: AggregatedColumnJSON['transformation_type'],
  data_source_index: number,
  column_name: string
): AggregatedColumnJSON {
  return {
    type: 'AggregatedColumn',
    transformation_type,
    data_source_index,
    column_name,
  };
}

export function makeEntityPropertyColumn(
  entity: EntityTypes,
  data_source_index: number,
  property: string
): EntityPropertyColumnJSON {
  return {
    type: 'EntityPropertyColumn',
    entity,
    data_source_index,
    property,
  };
}

export function makeFreeColumn(value: string, name: string): FreeColumnJSON {
  return {
    type: 'FreeColumn',
    column_name: name,
    value,
  };
}

export function makeDataSeriesColumn(
  data_source_index: number,
  column_name: string
): DataSeriesColumnJSON {
  return {
    type: 'DataSeriesColumn',
    data_source_index,
    column_name,
  };
}

// -- type guards

function isDataSeriesTable(
  config: TableJSON['config']
): config is DataSeriesTableJSON {
  return config.type === 'DataSeriesTable';
}

function isDecimatedSeriesTable(
  config: TableJSON['config']
): config is DecimatedSeriesTableJSON {
  return config.type === 'DecimatedSeriesTable';
}

export function isAggregatedTable(
  config: TableJSON['config']
): config is AggregatedTableJSON {
  return config.type === 'AggregatedTable';
}

export function isAggregatedColumn(
  column: AggregatedColumnJSON | EntityPropertyColumnJSON | FreeColumnJSON
): column is AggregatedColumnJSON {
  return column.type === 'AggregatedColumn';
}

export function isEntityPropertyColumn(
  column: AggregatedColumnJSON | EntityPropertyColumnJSON | FreeColumnJSON
): column is EntityPropertyColumnJSON {
  return column.type === 'EntityPropertyColumn';
}

export function isFreeColumn(
  column: AggregatedColumnJSON | EntityPropertyColumnJSON | FreeColumnJSON
): column is FreeColumnJSON {
  return column.type === 'FreeColumn';
}

export function isTableElement(element: ApiDashboardElement): element is Table {
  return element.element_type === 'table';
}

// -- getters and setters

function getHeadLimit(table: DataSeriesTableJSON): number | undefined {
  return table.headLimit?.count || undefined;
}

function setHeadLimit(
  table: DataSeriesTableJSON,
  count: number
): DataSeriesTableJSON {
  return {
    ...table,
    headLimit: {
      type: 'head',
      count,
    },
  };
}

function updateHeadLimit(
  table: DataSeriesTableJSON,
  updateFn: (count: number) => number
): DataSeriesTableJSON {
  const headLimit = getHeadLimit(table);
  if (headLimit) {
    return setHeadLimit(table, updateFn(headLimit));
  }
  return table;
}

function getMiddleLimit(table: DataSeriesTableJSON): number | undefined {
  return table.middleLimit?.count || undefined;
}

function setMiddleLimit(
  table: DataSeriesTableJSON,
  count: number
): DataSeriesTableJSON {
  return {
    ...table,
    middleLimit: {
      type: 'middle',
      count,
    },
  };
}

function updateMiddleLimit(
  table: DataSeriesTableJSON,
  updateFn: (count: number) => number
): DataSeriesTableJSON {
  const middleLimit = getMiddleLimit(table);
  if (middleLimit) {
    return setMiddleLimit(table, updateFn(middleLimit));
  }
  return table;
}

function getTailLimit(table: DataSeriesTableJSON): number | undefined {
  return table.tailLimit?.count || undefined;
}

function setTailLimit(
  table: DataSeriesTableJSON,
  count: number
): DataSeriesTableJSON {
  return {
    ...table,
    tailLimit: {
      type: 'tail',
      count,
    },
  };
}

function updateTailLimit(
  table: DataSeriesTableJSON,
  updateFn: (count: number) => number
): DataSeriesTableJSON {
  const tailLimit = getTailLimit(table);
  if (tailLimit) {
    return setTailLimit(table, updateFn(tailLimit));
  }
  return table;
}

function getDecimation(
  table: DecimatedSeriesTableJSON
): [Decimation['method'], number] {
  const decimation = table.decimation;
  return [decimation.method, decimation.intervalInSeconds];
}

function setDecimation(
  table: DecimatedSeriesTableJSON,
  method: Decimation['method'],
  intervalInSeconds: number
): DecimatedSeriesTableJSON {
  return {
    ...table,
    decimation: {
      type: 'decimation',
      method,
      intervalInSeconds,
    },
  };
}

function updateDecimation(
  table: DecimatedSeriesTableJSON,
  updateFn: (
    method: Decimation['method'],
    intervalInSeconds: number
  ) => [Decimation['method'], number]
): DecimatedSeriesTableJSON {
  const [previousMethod, previousIntervalInSeconds] = getDecimation(table);
  const [method, intervalInSeconds] = updateFn(
    previousMethod,
    previousIntervalInSeconds
  );
  return setDecimation(table, method, intervalInSeconds);
}

function getDataSeriesColumns(
  table: DataSeriesTableJSON | DecimatedSeriesTableJSON
): DataSeriesColumnJSON[] {
  return table.columns;
}

function getDataSeriesColumnAt(
  table: DataSeriesTableJSON | DecimatedSeriesTableJSON,
  index: number
): DataSeriesColumnJSON | undefined {
  return getDataSeriesColumns(table)[index];
}

function findDataSeriesColumn(
  table: DataSeriesTableJSON | DecimatedSeriesTableJSON,
  predicate: (column: DataSeriesColumnJSON) => boolean
): DataSeriesColumnJSON | undefined {
  return getDataSeriesColumns(table).find(predicate);
}

function setDataSeriesColumns<
  T extends DataSeriesTableJSON | DecimatedSeriesTableJSON
>(table: T, columns: DataSeriesColumnJSON[]): T {
  return {
    ...table,
    columns,
  };
}

function setDataSeriesColumnAt<
  T extends DataSeriesTableJSON | DecimatedSeriesTableJSON
>(table: T, index: number, column: DataSeriesColumnJSON): T {
  const columns = [...getDataSeriesColumns(table)];
  columns[index] = column;
  return setDataSeriesColumns(table, columns);
}

function updateDataSeriesColumns<
  T extends DataSeriesTableJSON | DecimatedSeriesTableJSON
>(
  table: T,
  updateFn: (columns: DataSeriesColumnJSON[]) => DataSeriesColumnJSON[]
): T {
  return setDataSeriesColumns(table, updateFn(getDataSeriesColumns(table)));
}

function updateDataSeriesColumnAt<
  T extends DataSeriesTableJSON | DecimatedSeriesTableJSON
>(
  table: T,
  index: number,
  updateFn: (column: DataSeriesColumnJSON) => DataSeriesColumnJSON
): T {
  const columns = [...getDataSeriesColumns(table)];
  if (columns[index]) columns[index] = updateFn(columns[index]);
  return setDataSeriesColumns(table, columns);
}

function updateEachDataSeriesColumns<
  T extends DataSeriesTableJSON | DecimatedSeriesTableJSON
>(
  table: T,
  updateFn: (column: DataSeriesColumnJSON) => DataSeriesColumnJSON
): T {
  return setDataSeriesColumns(table, getDataSeriesColumns(table).map(updateFn));
}

function insertDataSeriesColumnAt<
  T extends DataSeriesTableJSON | DecimatedSeriesTableJSON
>(table: T, index: number, column: DataSeriesColumnJSON): T {
  const columns = [...getDataSeriesColumns(table)];
  columns.splice(index, 0, column);
  return setDataSeriesColumns(table, columns);
}

function removeDataSeriesColumnAt<
  T extends DataSeriesTableJSON | DecimatedSeriesTableJSON
>(table: T, index: number): T {
  const columns = [...getDataSeriesColumns(table)];
  columns.splice(index, 1);
  return setDataSeriesColumns(table, columns);
}

function removeDataSeriesColumnByDataSourceIndex<
  T extends DataSeriesTableJSON | DecimatedSeriesTableJSON
>(table: T, data_source_index: number): T {
  return setDataSeriesColumns(
    table,
    getDataSeriesColumns(table).filter(
      (column) => column.data_source_index !== data_source_index
    )
  );
}

function getAggregationTableColumns(
  table: AggregatedTableJSON
): (AggregatedColumnJSON | EntityPropertyColumnJSON | FreeColumnJSON)[][] {
  return table.rows.map((row) => [...row.columns]);
}

function getAggregationTableColumnsByPredicate(
  table: AggregatedTableJSON,
  predicate: (
    col: AggregatedColumnJSON | EntityPropertyColumnJSON | FreeColumnJSON
  ) => boolean
): (AggregatedColumnJSON | EntityPropertyColumnJSON | FreeColumnJSON)[][] {
  return getAggregationTableColumns(table).map((row) => row.filter(predicate));
}

function getAggregationTableColumnsAtRow(
  table: AggregatedTableJSON,
  row_index: number
): (AggregatedColumnJSON | EntityPropertyColumnJSON | FreeColumnJSON)[] {
  return getAggregationTableColumns(table)[row_index] ?? [];
}

function getAggregationTableColumnAtEachRow(
  table: AggregatedTableJSON,
  column_index: number
): (AggregatedColumnJSON | EntityPropertyColumnJSON | FreeColumnJSON)[] {
  return getAggregationTableColumns(table)
    .map((columns) => columns[column_index])
    .filter(
      (column): column is AggregatedColumnJSON | EntityPropertyColumnJSON =>
        !!column
    );
}

function getAggregationTableColumnAt(
  table: AggregatedTableJSON,
  row_index: number,
  column_index: number
):
  | AggregatedColumnJSON
  | EntityPropertyColumnJSON
  | FreeColumnJSON
  | undefined {
  return getAggregationTableColumnsAtRow(table, row_index)[column_index];
}

function getFreeColumnAt(
  table: AggregatedTableJSON,
  row_index: number,
  column_index: number
): FreeColumnJSON | undefined {
  return getAggregationTableColumnsAtRow(table, row_index)[column_index] as
    | FreeColumnJSON
    | undefined;
}

function setAggregationTableColumns(
  table: AggregatedTableJSON,
  columns: (
    | AggregatedColumnJSON
    | EntityPropertyColumnJSON
    | FreeColumnJSON
  )[][]
): AggregatedTableJSON {
  return {
    ...table,
    rows: columns.map((columns) => ({
      type: 'AggregationRow',
      columns,
    })),
  };
}

function setAggregationTableColumnsAtRow(
  table: AggregatedTableJSON,
  row_index: number,
  columns: (AggregatedColumnJSON | EntityPropertyColumnJSON | FreeColumnJSON)[]
): AggregatedTableJSON {
  const rows = [...getAggregationTableColumns(table)];
  if (rows[row_index]) rows[row_index] = columns;
  return setAggregationTableColumns(table, rows);
}

function setAggregationTableColumnAtEachRow(
  table: AggregatedTableJSON,
  column_index: number,
  column: AggregatedColumnJSON | EntityPropertyColumnJSON | FreeColumnJSON
): AggregatedTableJSON {
  return setAggregationTableColumns(
    table,
    getAggregationTableColumns(table).map((columns) =>
      columns.map((_, i) => (i === column_index ? column : _))
    )
  );
}

function setAggregationTableColumnAt(
  table: AggregatedTableJSON,
  row_index: number,
  column_index: number,
  column: AggregatedColumnJSON | EntityPropertyColumnJSON | FreeColumnJSON
): AggregatedTableJSON {
  const rows = [...getAggregationTableColumns(table)];
  if (rows[row_index]) rows[row_index][column_index] = column;
  return setAggregationTableColumns(table, rows);
}

function setFreeColumnAt(
  table: AggregatedTableJSON,
  row_index: number,
  column_index: number,
  column: FreeColumnJSON
): AggregatedTableJSON {
  const rows = [...getAggregationTableColumns(table)];
  if (rows[row_index] && isFreeColumn(rows[row_index][column_index]))
    rows[row_index][column_index] = column;
  return setAggregationTableColumns(table, rows);
}

function updateAggregationTableColumns(
  table: AggregatedTableJSON,
  updateFn: (
    columns: (
      | AggregatedColumnJSON
      | EntityPropertyColumnJSON
      | FreeColumnJSON
    )[][]
  ) => (AggregatedColumnJSON | EntityPropertyColumnJSON)[][]
): AggregatedTableJSON {
  return setAggregationTableColumns(
    table,
    updateFn(getAggregationTableColumns(table))
  );
}

function updateAggregationTableColumnsAtRow(
  table: AggregatedTableJSON,
  row_index: number,
  updateFn: (
    columns: (
      | AggregatedColumnJSON
      | EntityPropertyColumnJSON
      | FreeColumnJSON
    )[]
  ) => (AggregatedColumnJSON | EntityPropertyColumnJSON)[]
): AggregatedTableJSON {
  const row = getAggregationTableColumnsAtRow(table, row_index);
  if (row)
    return setAggregationTableColumnsAtRow(table, row_index, updateFn(row));
  return table;
}

function updateAggregationTableColumnAt(
  table: AggregatedTableJSON,
  row_index: number,
  column_index: number,
  updateFn: (
    column: AggregatedColumnJSON | EntityPropertyColumnJSON | FreeColumnJSON
  ) => AggregatedColumnJSON | EntityPropertyColumnJSON
): AggregatedTableJSON {
  const col = getAggregationTableColumnAt(table, row_index, column_index);
  if (col)
    return setAggregationTableColumnAt(
      table,
      row_index,
      column_index,
      updateFn(col)
    );
  return table;
}

function updateFreeColumnAt(
  table: AggregatedTableJSON,
  row_index: number,
  column_index: number,
  value: string
): AggregatedTableJSON {
  const col = getFreeColumnAt(table, row_index, column_index);
  if (col) {
    const newCol = {
      ...col,
      value,
    };
    return setAggregationTableColumnAt(table, row_index, column_index, newCol);
  }
  return table;
}

function updateFreeColumnNameAtEachRow(
  table: AggregatedTableJSON,
  column_index: number,
  name: string
): AggregatedTableJSON {
  return updateAggregationTableColumnAtEachRow(table, column_index, (col) => {
    if (!isFreeColumn(col)) return col;
    const newCol: FreeColumnJSON = {
      ...col,
      column_name: name,
    };
    return newCol;
  });
}

function updateAggregationTableColumnAtEachRow(
  table: AggregatedTableJSON,
  column_index: number,
  updateFn: (
    column: AggregatedColumnJSON | EntityPropertyColumnJSON | FreeColumnJSON
  ) => AggregatedColumnJSON | EntityPropertyColumnJSON | FreeColumnJSON
): AggregatedTableJSON {
  return setAggregationTableColumns(
    table,
    getAggregationTableColumns(table).map((columns) =>
      columns.map((col, i) => (i === column_index ? updateFn(col) : col))
    )
  );
}

function updateEachAggregationTableColumn(
  table: AggregatedTableJSON,
  updateFn: (
    column: AggregatedColumnJSON | EntityPropertyColumnJSON | FreeColumnJSON
  ) => AggregatedColumnJSON | EntityPropertyColumnJSON
): AggregatedTableJSON {
  return setAggregationTableColumns(
    table,
    getAggregationTableColumns(table).map((columns) => columns.map(updateFn))
  );
}

function updateEachAggregationTableRow(
  table: AggregatedTableJSON,
  updateFn: (
    columns: (
      | AggregatedColumnJSON
      | EntityPropertyColumnJSON
      | FreeColumnJSON
    )[]
  ) => (AggregatedColumnJSON | EntityPropertyColumnJSON | FreeColumnJSON)[]
): AggregatedTableJSON {
  return setAggregationTableColumns(
    table,
    getAggregationTableColumns(table).map(updateFn)
  );
}

function insertAggregationTableRowAt(
  table: AggregatedTableJSON,
  row_index: number,
  columns: (AggregatedColumnJSON | EntityPropertyColumnJSON | FreeColumnJSON)[]
): AggregatedTableJSON {
  const rows = [...getAggregationTableColumns(table)];
  rows.splice(row_index, 0, columns);
  return setAggregationTableColumns(table, rows);
}

function insertAggregationTableColumnAtEachRow(
  table: AggregatedTableJSON,
  column_index: number,
  column: AggregatedColumnJSON | EntityPropertyColumnJSON | FreeColumnJSON
): AggregatedTableJSON {
  return setAggregationTableColumns(
    table,
    getAggregationTableColumns(table).map((columns) => {
      const cols = [...columns];
      cols.splice(column_index, 0, column);
      return cols;
    })
  );
}

/**
 * @description Variant of `insertAggregationTableColumnAtEachRow` that only accept `FreeColumn`s
 */
const insertFreeColumnAtEachRow: (
  table: AggregatedTableJSON,
  column_index: number,
  column: FreeColumnJSON
) => AggregatedTableJSON = insertAggregationTableColumnAtEachRow;

function leftAppendAggregationTableColumnAtEachRow(
  table: AggregatedTableJSON,
  column: AggregatedColumnJSON | EntityPropertyColumnJSON | FreeColumnJSON
): AggregatedTableJSON {
  return setAggregationTableColumns(
    table,
    getAggregationTableColumns(table).map((columns) => [column, ...columns])
  );
}

function rightAppendAggregationTableColumnAtEachRow(
  table: AggregatedTableJSON,
  column: AggregatedColumnJSON | EntityPropertyColumnJSON | FreeColumnJSON
): AggregatedTableJSON {
  return setAggregationTableColumns(
    table,
    getAggregationTableColumns(table).map((columns) => [...columns, column])
  );
}

function removeAggregationTableRowAt(
  table: AggregatedTableJSON,
  row_index: number
): AggregatedTableJSON {
  const rows = [...getAggregationTableColumns(table)];
  rows.splice(row_index, 1);
  return setAggregationTableColumns(table, rows);
}

function removeAggregationTableColumnAtEachRow(
  table: AggregatedTableJSON,
  column_index: number
): AggregatedTableJSON {
  return setAggregationTableColumns(
    table,
    getAggregationTableColumns(table).map((columns) =>
      columns.filter((_, i) => i !== column_index)
    )
  );
}

function upAppendAggregationTableRow(
  table: AggregatedTableJSON,
  columns: (AggregatedColumnJSON | EntityPropertyColumnJSON | FreeColumnJSON)[]
): AggregatedTableJSON {
  return setAggregationTableColumns(table, [
    columns,
    ...getAggregationTableColumns(table),
  ]);
}

function downAppendAggregationTableRow(
  table: AggregatedTableJSON,
  columns: (AggregatedColumnJSON | EntityPropertyColumnJSON | FreeColumnJSON)[]
): AggregatedTableJSON {
  return setAggregationTableColumns(table, [
    ...getAggregationTableColumns(table),
    columns,
  ]);
}

// -- Table agnostic getters and setters

export function removeColumnAt(
  table: TableConfigured,
  index: number
): TableConfigured {
  if (
    table.properties.config.type === 'DataSeriesTable' ||
    table.properties.config.type === 'DecimatedSeriesTable'
  ) {
    return {
      ...table,
      properties: {
        ...table.properties,
        config: removeDataSeriesColumnAt(table.properties.config, index),
      },
    };
  }
  return {
    ...table,
    properties: {
      ...table.properties,
      config: removeAggregationTableColumnAtEachRow(
        table.properties.config,
        index
      ),
    },
  };
}

export function removeColumnByDataSourceIndex(
  table: TableConfigured,
  data_source_index: number
): TableConfigured {
  if (
    table.properties.config.type === 'DataSeriesTable' ||
    table.properties.config.type === 'DecimatedSeriesTable'
  ) {
    return {
      ...table,
      properties: {
        ...table.properties,
        config: removeDataSeriesColumnByDataSourceIndex(
          table.properties.config,
          data_source_index
        ),
      },
    };
  }
  return {
    ...table,
    properties: {
      ...table.properties,
      config: removeAggregationTableColumnAtEachRow(
        table.properties.config,
        data_source_index
      ),
    },
  };
}

// -- Transformations

function makeAggregatedRow(
  entity: EntityTypes,
  entityProperty: string,
  dataSourceIndex: number
): AggregationRowJSON {
  return {
    type: 'AggregationRow',
    columns: [
      {
        type: 'EntityPropertyColumn',
        entity,
        data_source_index: dataSourceIndex,
        property: entityProperty,
      },
      {
        type: 'AggregatedColumn',
        transformation_type: 'min',
        data_source_index: dataSourceIndex,
        column_name: `min`,
      },
      {
        type: 'AggregatedColumn',
        transformation_type: 'max',
        data_source_index: dataSourceIndex,
        column_name: `max`,
      },
      {
        type: 'AggregatedColumn',
        transformation_type: 'mean',
        data_source_index: dataSourceIndex,
        column_name: `moyenne`,
      },
      {
        type: 'AggregatedColumn',
        transformation_type: 'sum',
        data_source_index: dataSourceIndex,
        column_name: `somme`,
      },
      {
        type: 'AggregatedColumn',
        transformation_type: 'count',
        data_source_index: dataSourceIndex,
        column_name: `nombre`,
      },
    ],
  };
}

function makeAggregatedRows(
  dataSources: { measure_point_id: number; measure_type_id: number }[]
): AggregationRowJSON[] {
  const allMeasureTypesAreTheSame = mapEqual(
    dataSources,
    (s) => s.measure_type_id
  );
  const allMeasurePointsAreTheSame = mapEqual(
    dataSources,
    (s) => s.measure_point_id
  );
  if (!allMeasureTypesAreTheSame) {
    const entity = 'measure_type';
    const entityProperty = 'name';
    return dataSources.map((_, i) =>
      makeAggregatedRow(entity, entityProperty, i)
    );
  }
  if (!allMeasurePointsAreTheSame) {
    const entity = 'measure_point';
    const entityProperty = 'name';
    return dataSources.map((_, i) =>
      makeAggregatedRow(entity, entityProperty, i)
    );
  }
  if (dataSources.length > 0) {
    return [makeAggregatedRow('measure_type', 'name', 0)];
  }
  return [];
}

function makeDataSeriesColumns(
  dataSources: ApiDashboardElement['data_source'],
  names: string[]
): DataSeriesColumnJSON[] {
  return dataSources.map((_, i) => ({
    type: 'DataSeriesColumn',
    data_source_index: i,
    column_name: names[i] || `Source de données ${i}`,
  }));
}

function dataSeriesTableToDecimatedTable(
  _table: DataSeriesTableJSON,
  method: Decimation['method'],
  intervalInSeconds: number,
  dataSources: ApiDashboardElement['data_source'],
  names: string[]
): DecimatedSeriesTableJSON {
  return {
    type: 'DecimatedSeriesTable',
    columns: makeDataSeriesColumns(dataSources, names),
    decimation: {
      type: 'decimation',
      method,
      intervalInSeconds,
    },
  };
}

function decimatedTableToDataSeriesTable(
  _table: DecimatedSeriesTableJSON,
  dataSources: ApiDashboardElement['data_source'],
  names: string[],
  count = 10
): DataSeriesTableJSON {
  return {
    type: 'DataSeriesTable',
    columns: makeDataSeriesColumns(dataSources, names),
    headLimit: {
      type: 'head',
      count,
    },
    middleLimit: {
      type: 'middle',
      count,
    },
    tailLimit: {
      type: 'tail',
      count,
    },
  };
}

function dataSeriesTableToAggregatedTable(
  _table: DataSeriesTableJSON,
  dataSources: SelectionSchemaMeasurePoint[]
): AggregatedTableJSON {
  return {
    type: 'AggregatedTable',
    rows: makeAggregatedRows(dataSources),
  };
}

function aggregatedTableToDataSeriesTable(
  _table: AggregatedTableJSON,
  dataSources: ApiDashboardElement['data_source'],
  names: string[],
  count = 10
): DataSeriesTableJSON {
  return {
    type: 'DataSeriesTable',
    columns: makeDataSeriesColumns(dataSources, names),
    headLimit: {
      type: 'head',
      count,
    },
    middleLimit: {
      type: 'middle',
      count,
    },
    tailLimit: {
      type: 'tail',
      count,
    },
  };
}

function decimatedTableToAggregatedTable(
  _table: DecimatedSeriesTableJSON,
  dataSources: SelectionSchemaMeasurePoint[]
): AggregatedTableJSON {
  return {
    type: 'AggregatedTable',
    rows: makeAggregatedRows(dataSources),
  };
}

function aggregatedTableToDecimatedTable(
  _table: AggregatedTableJSON,
  method: Decimation['method'],
  intervalInSeconds: number,
  dataSources: ApiDashboardElement['data_source'],
  names: string[]
): DecimatedSeriesTableJSON {
  return {
    type: 'DecimatedSeriesTable',
    columns: makeDataSeriesColumns(dataSources, names),
    decimation: {
      type: 'decimation',
      method,
      intervalInSeconds,
    },
  };
}

function convertTableTypeAtConfigLevel(
  table: AggregatedTableJSON | DataSeriesTableJSON | DecimatedSeriesTableJSON,
  type: 'DataSeriesTable' | 'DecimatedSeriesTable' | 'AggregatedTable',
  dataSources: { measure_point_id: number; measure_type_id: number }[],
  names: string[]
): AggregatedTableJSON | DataSeriesTableJSON | DecimatedSeriesTableJSON {
  if (table.type === type) {
    return table;
  }
  if (type === 'DataSeriesTable' && table.type === 'DecimatedSeriesTable') {
    return decimatedTableToDataSeriesTable(table, dataSources, names);
  } else if (
    type === 'DecimatedSeriesTable' &&
    table.type === 'DataSeriesTable'
  ) {
    return dataSeriesTableToDecimatedTable(
      table,
      'mean',
      60,
      dataSources,
      names
    );
  } else if (type === 'DataSeriesTable' && table.type === 'AggregatedTable') {
    return aggregatedTableToDataSeriesTable(table, dataSources, names);
  } else if (type === 'AggregatedTable' && table.type === 'DataSeriesTable') {
    return dataSeriesTableToAggregatedTable(table, dataSources);
  } else if (
    type === 'DecimatedSeriesTable' &&
    table.type === 'AggregatedTable'
  ) {
    return aggregatedTableToDecimatedTable(
      table,
      'mean',
      60,
      dataSources,
      names
    );
  } else if (
    type === 'AggregatedTable' &&
    table.type === 'DecimatedSeriesTable'
  ) {
    return decimatedTableToAggregatedTable(table, dataSources);
  }
  throw new Error(`Cannot convert table type from ${table.type} to ${type}`);
}

export function convertTableType(
  table: TableConfigured,
  type: 'DataSeriesTable' | 'DecimatedSeriesTable' | 'AggregatedTable',
  namesFn: (dataSources: ApiDashboardElement['data_source']) => string[]
): TableConfigured {
  return {
    ...table,
    properties: {
      ...table.properties,
      config: convertTableTypeAtConfigLevel(
        table.properties.config,
        type,
        table.data_source,
        namesFn(table.data_source)
      ),
    },
  };
}

export function makeTable(
  config: AggregatedTableJSON | DataSeriesTableJSON | DecimatedSeriesTableJSON,
  startDate: string,
  endDate: string,
  name = 'Nouvelle Table',
  data_source: TableConfigured['data_source'] = []
): TableConfigured {
  return {
    id: 0,
    element_type: 'table',
    name,
    data_source,
    properties: {
      config,
      state_type: 'configured_table',
      intervals: [
        {
          type: 'fixed_time_range',
          name: '',
          campaign_ids: null,
          startdate: startDate,
          enddate: endDate,
        },
      ],
      x: 0,
      y: 0,
      w: 12,
      h: 9,
    },
    resource_ids: null,
  };
}

interface MakeTableAgnosticArgs {
  <
    T extends
      | DataSeriesTableJSON
      | DecimatedSeriesTableJSON
      | AggregatedTableJSON
  >(
    fn: (table: T) => T
  ): (table: Table) => Table;
  <
    T extends
      | DataSeriesTableJSON
      | DecimatedSeriesTableJSON
      | AggregatedTableJSON,
    U
  >(
    fn: (table: T, u: U) => T
  ): (table: Table, u: U) => Table;
  <
    T extends
      | DataSeriesTableJSON
      | DecimatedSeriesTableJSON
      | AggregatedTableJSON,
    U,
    V
  >(
    fn: (table: T, u: U, v: V) => T
  ): (table: Table, u: U, v: V) => Table;
  <
    T extends
      | DataSeriesTableJSON
      | DecimatedSeriesTableJSON
      | AggregatedTableJSON,
    U,
    V,
    W
  >(
    fn: (table: T, u: U, v: V, w: W) => T
  ): (table: Table, u: U, v: V, w: W) => Table;
  <
    T extends
      | DataSeriesTableJSON
      | DecimatedSeriesTableJSON
      | AggregatedTableJSON,
    U,
    V,
    W,
    X
  >(
    fn: (table: T, u: U, v: V, w: W, x: X) => T
  ): (table: Table, u: U, v: V, w: W, x: X) => Table;
  <
    T extends
      | DataSeriesTableJSON
      | DecimatedSeriesTableJSON
      | AggregatedTableJSON,
    U,
    V,
    W,
    X,
    Y
  >(
    fn: (table: T, u: U, v: V, w: W, x: X, y: Y) => T
  ): (table: Table, u: U, v: V, w: W, x: X, y: Y) => Table;
  <
    T extends
      | DataSeriesTableJSON
      | DecimatedSeriesTableJSON
      | AggregatedTableJSON,
    U,
    V,
    W,
    X,
    Y,
    Z
  >(
    fn: (table: T, u: U, v: V, w: W, x: X, y: Y, z: Z) => T
  ): (table: Table, u: U, v: V, w: W, x: X, y: Y, z: Z) => Table;
}

const makeTableAgnosticSetter: (
  tableType: TableConfigured['properties']['config']['type']
) => MakeTableAgnosticArgs =
  (tableType) =>
  (fn: any) =>
  (table: Table, ...args: any[]) => {
    if (
      isConfiguredTable(table) &&
      table.properties.config.type === tableType
    ) {
      return {
        ...table,
        properties: {
          ...table.properties,
          config: fn(table.properties.config, ...args),
        },
      };
    }
    return table;
  };

interface MakeTableAgnosticGetterArgs {
  <
    T extends
      | DataSeriesTableJSON
      | DecimatedSeriesTableJSON
      | AggregatedTableJSON,
    R
  >(
    fn: (table: T) => R
  ): (table: TableConfigured) => R;
  <
    T extends
      | DataSeriesTableJSON
      | DecimatedSeriesTableJSON
      | AggregatedTableJSON,
    R,
    U
  >(
    fn: (table: T, u: U) => R
  ): (table: TableConfigured, u: U) => R;
  <
    T extends
      | DataSeriesTableJSON
      | DecimatedSeriesTableJSON
      | AggregatedTableJSON,
    R,
    U,
    V
  >(
    fn: (table: T, u: U, v: V) => R
  ): (table: TableConfigured, u: U, v: V) => R;
  <
    T extends
      | DataSeriesTableJSON
      | DecimatedSeriesTableJSON
      | AggregatedTableJSON,
    R,
    U,
    V,
    W
  >(
    fn: (table: T, u: U, v: V, w: W) => R
  ): (table: TableConfigured, u: U, v: V, w: W) => R;
  <
    T extends
      | DataSeriesTableJSON
      | DecimatedSeriesTableJSON
      | AggregatedTableJSON,
    R,
    U,
    V,
    W,
    X
  >(
    fn: (table: T, u: U, v: V, w: W, x: X) => R
  ): (table: TableConfigured, u: U, v: V, w: W, x: X) => R;
  <
    T extends
      | DataSeriesTableJSON
      | DecimatedSeriesTableJSON
      | AggregatedTableJSON,
    R,
    U,
    V,
    W,
    X,
    Y
  >(
    fn: (table: T, u: U, v: V, w: W, x: X, y: Y) => R
  ): (table: TableConfigured, u: U, v: V, w: W, x: X, y: Y) => R;
  <
    T extends
      | DataSeriesTableJSON
      | DecimatedSeriesTableJSON
      | AggregatedTableJSON,
    R,
    U,
    V,
    W,
    X,
    Y,
    Z
  >(
    fn: (table: T, u: U, v: V, w: W, x: X, y: Y, z: Z) => R
  ): (table: TableConfigured, u: U, v: V, w: W, x: X, y: Y, z: Z) => R;
}

const makeTableAgnosticGetter: (
  tableType: TableConfigured['properties']['config']['type']
) => MakeTableAgnosticGetterArgs =
  (tableType) =>
  (fn: any) =>
  (table: Table, ...args: any[]) => {
    if (
      isConfiguredTable(table) &&
      table.properties.config.type === tableType
    ) {
      return fn(table.properties.config, ...args);
    }
    return undefined;
  };

export const hasDataSeriesTableConfig =
  makeTableAgnosticGetter('DataSeriesTable')(isDataSeriesTable);
export const hasDecimatedSeriesTableConfig = makeTableAgnosticGetter(
  'DecimatedSeriesTable'
)(isDecimatedSeriesTable);
export const hasAggregatedTableConfig =
  makeTableAgnosticGetter('AggregatedTable')(isAggregatedTable);

export const getHeadLimitG =
  makeTableAgnosticGetter('DataSeriesTable')(getHeadLimit);
export const setHeadLimitG =
  makeTableAgnosticSetter('DataSeriesTable')(setHeadLimit);
export const updateHeadLimitG =
  makeTableAgnosticSetter('DataSeriesTable')(updateHeadLimit);

export const getMiddleLimitG =
  makeTableAgnosticGetter('DataSeriesTable')(getMiddleLimit);
export const setMiddleLimitG =
  makeTableAgnosticSetter('DataSeriesTable')(setMiddleLimit);
export const updateMiddleLimitG =
  makeTableAgnosticSetter('DataSeriesTable')(updateMiddleLimit);

export const getTailLimitG =
  makeTableAgnosticGetter('DataSeriesTable')(getTailLimit);
export const setTailLimitG =
  makeTableAgnosticSetter('DataSeriesTable')(setTailLimit);
export const updateTailLimitG =
  makeTableAgnosticSetter('DataSeriesTable')(updateTailLimit);

export const getDecimationG = makeTableAgnosticGetter('DecimatedSeriesTable')(
  getDecimation
);
export const setDecimationG = makeTableAgnosticSetter('DecimatedSeriesTable')(
  setDecimation
);
export const updateDecimationG = makeTableAgnosticSetter(
  'DecimatedSeriesTable'
)(updateDecimation);

export const getDataSeriesColumnsG =
  makeTableAgnosticGetter('DataSeriesTable')(getDataSeriesColumns);
export const getDataSeriesColumnAtG = makeTableAgnosticGetter(
  'DataSeriesTable'
)(getDataSeriesColumnAt);
export const findDataSeriesColumnG =
  makeTableAgnosticGetter('DataSeriesTable')(findDataSeriesColumn);
export const setDataSeriesColumnsG =
  makeTableAgnosticSetter('DataSeriesTable')(setDataSeriesColumns);
export const setDataSeriesColumnAtG = makeTableAgnosticSetter(
  'DataSeriesTable'
)(setDataSeriesColumnAt);
export const updateDataSeriesColumnsG = makeTableAgnosticSetter(
  'DataSeriesTable'
)(updateDataSeriesColumns);
export const updateDataSeriesColumnAtG = makeTableAgnosticSetter(
  'DataSeriesTable'
)(updateDataSeriesColumnAt);
export const updateEachDataSeriesColumnsG = makeTableAgnosticSetter(
  'DataSeriesTable'
)(updateEachDataSeriesColumns);
export const insertDataSeriesColumnAtG = makeTableAgnosticSetter(
  'DataSeriesTable'
)(insertDataSeriesColumnAt);
export const removeDataSeriesColumnAtG = makeTableAgnosticSetter(
  'DataSeriesTable'
)(removeDataSeriesColumnAt);
export const removeDataSeriesColumnByDataSourceIndexG = makeTableAgnosticSetter(
  'DataSeriesTable'
)(removeDataSeriesColumnByDataSourceIndex);

export const getAggregationTableColumnsG = makeTableAgnosticGetter(
  'AggregatedTable'
)(getAggregationTableColumns);
export const getAggregationTableColumnsByPredicateG = makeTableAgnosticGetter(
  'AggregatedTable'
)(getAggregationTableColumnsByPredicate);
export const getAggregationTableColumnsAtRowG = makeTableAgnosticGetter(
  'AggregatedTable'
)(getAggregationTableColumnsAtRow);
export const getAggregationTableColumnAtEachRowG = makeTableAgnosticGetter(
  'AggregatedTable'
)(getAggregationTableColumnAtEachRow);
export const getAggregationTableColumnAtG = makeTableAgnosticGetter(
  'AggregatedTable'
)(getAggregationTableColumnAt);
export const getFreeColumnAtG =
  makeTableAgnosticGetter('AggregatedTable')(getFreeColumnAt);
export const setAggregationTableColumnsG = makeTableAgnosticSetter(
  'AggregatedTable'
)(setAggregationTableColumns);
export const setAggregationTableColumnsAtRowG = makeTableAgnosticSetter(
  'AggregatedTable'
)(setAggregationTableColumnsAtRow);
export const setAggregationTableColumnAtEachRowG = makeTableAgnosticSetter(
  'AggregatedTable'
)(setAggregationTableColumnAtEachRow);
export const setAggregationTableColumnAtG = makeTableAgnosticSetter(
  'AggregatedTable'
)(setAggregationTableColumnAt);
export const setFreeColumnAtG =
  makeTableAgnosticSetter('AggregatedTable')(setFreeColumnAt);
export const updateAggregationTableColumnsG = makeTableAgnosticSetter(
  'AggregatedTable'
)(updateAggregationTableColumns);
export const updateAggregationTableColumnsAtRowG = makeTableAgnosticSetter(
  'AggregatedTable'
)(updateAggregationTableColumnsAtRow);
export const updateAggregationTableColumnAtG = makeTableAgnosticSetter(
  'AggregatedTable'
)(updateAggregationTableColumnAt);
export const updateFreeColumnAtG =
  makeTableAgnosticSetter('AggregatedTable')(updateFreeColumnAt);
export const updateFreeColumnAtEachRowG = makeTableAgnosticSetter(
  'AggregatedTable'
)(updateFreeColumnNameAtEachRow);
export const updateAggregationTableColumnAtEachRowG = makeTableAgnosticSetter(
  'AggregatedTable'
)(updateAggregationTableColumnAtEachRow);
export const updateEachAggregationTableColumnG = makeTableAgnosticSetter(
  'AggregatedTable'
)(updateEachAggregationTableColumn);
export const updateEachAggregationTableRowG = makeTableAgnosticSetter(
  'AggregatedTable'
)(updateEachAggregationTableRow);
export const insertAggregationTableRowAtG = makeTableAgnosticSetter(
  'AggregatedTable'
)(insertAggregationTableRowAt);
export const insertAggregationTableColumnAtEachRowG = makeTableAgnosticSetter(
  'AggregatedTable'
)(insertAggregationTableColumnAtEachRow);
export const insertFreeColumnAtEachRowG = makeTableAgnosticSetter(
  'AggregatedTable'
)(insertFreeColumnAtEachRow);
export const leftAppendAggregationTableColumnAtEachRowG =
  makeTableAgnosticSetter('AggregatedTable')(
    leftAppendAggregationTableColumnAtEachRow
  );
export const rightAppendAggregationTableColumnAtEachRowG =
  makeTableAgnosticSetter('AggregatedTable')(
    rightAppendAggregationTableColumnAtEachRow
  );
export const removeAggregationTableRowAtG = makeTableAgnosticSetter(
  'AggregatedTable'
)(removeAggregationTableRowAt);
export const removeAggregationTableColumnAtEachRowG = makeTableAgnosticSetter(
  'AggregatedTable'
)(removeAggregationTableColumnAtEachRow);
export const upAppendAggregationTableRowG = makeTableAgnosticSetter(
  'AggregatedTable'
)(upAppendAggregationTableRow);
export const downAppendAggregationTableRowG = makeTableAgnosticSetter(
  'AggregatedTable'
)(downAppendAggregationTableRow);

interface MakeApiDashboardElementGetterArgs {
  <T extends Table, R>(fn: (table: T) => R): (
    element: ApiDashboardElement
  ) => R;
  <T extends Table, U, R>(fn: (table: T, u: U) => R): (
    element: ApiDashboardElement,
    u: U
  ) => R;
  <T extends Table, U, V, R>(fn: (table: T, u: U, v: V) => R): (
    element: ApiDashboardElement,
    u: U,
    v: V
  ) => R;
  <T extends Table, U, V, W, R>(fn: (table: T, u: U, v: V, w: W) => R): (
    element: ApiDashboardElement,
    u: U,
    v: V,
    w: W
  ) => R;
  <T extends Table, U, V, W, X, R>(
    fn: (table: T, u: U, v: V, w: W, x: X) => R
  ): (element: ApiDashboardElement, u: U, v: V, w: W, x: X) => R;
  <T extends Table, U, V, W, X, Y, R>(
    fn: (table: T, u: U, v: V, w: W, x: X, y: Y) => R
  ): (element: ApiDashboardElement, u: U, v: V, w: W, x: X, y: Y) => R;
  <T extends Table, U, V, W, X, Y, Z, R>(
    fn: (table: T, u: U, v: V, w: W, x: X, y: Y, z: Z) => R
  ): (element: ApiDashboardElement, u: U, v: V, w: W, x: X, y: Y, z: Z) => R;
}

interface MakeApiDashboardElementSetterArgs {
  <T extends Table>(fn: (table: T) => T): (
    element: ApiDashboardElement
  ) => ApiDashboardElement;
  <T extends Table, U>(fn: (table: T, u: U) => T): (
    element: ApiDashboardElement,
    u: U
  ) => ApiDashboardElement;
  <T extends Table, U, V>(fn: (table: T, u: U, v: V) => T): (
    element: ApiDashboardElement,
    u: U,
    v: V
  ) => ApiDashboardElement;
  <T extends Table, U, V, W>(fn: (table: T, u: U, v: V, w: W) => T): (
    element: ApiDashboardElement,
    u: U,
    v: V,
    w: W
  ) => ApiDashboardElement;
  <T extends Table, U, V, W, X>(fn: (table: T, u: U, v: V, w: W, x: X) => T): (
    element: ApiDashboardElement,
    u: U,
    v: V,
    w: W,
    x: X
  ) => ApiDashboardElement;
  <T extends Table, U, V, W, X, Y>(
    fn: (table: T, u: U, v: V, w: W, x: X, y: Y) => T
  ): (
    element: ApiDashboardElement,
    u: U,
    v: V,
    w: W,
    x: X,
    y: Y
  ) => ApiDashboardElement;
  <T extends Table, U, V, W, X, Y, Z>(
    fn: (table: T, u: U, v: V, w: W, x: X, y: Y, z: Z) => T
  ): (
    element: ApiDashboardElement,
    u: U,
    v: V,
    w: W,
    x: X,
    y: Y,
    z: Z
  ) => ApiDashboardElement;
}

const makeApiDashboardElementGetter: MakeApiDashboardElementGetterArgs =
  (fn: any) =>
  (element: ApiDashboardElement, ...args: any[]) => {
    if (isTableElement(element)) {
      return fn(element, ...args);
    }
    return undefined;
  };

const makeApiDashboardElementSetter: MakeApiDashboardElementSetterArgs =
  (fn: any) =>
  (element: ApiDashboardElement, ...args: any[]) => {
    if (isTableElement(element)) {
      return fn(element, ...args);
    }
    return element;
  };

export const getHeadLimitE = makeApiDashboardElementGetter(getHeadLimitG);
export const setHeadLimitE = makeApiDashboardElementSetter(setHeadLimitG);
export const updateHeadLimitE = makeApiDashboardElementSetter(updateHeadLimitG);

export const getMiddleLimitE = makeApiDashboardElementGetter(getMiddleLimitG);
export const setMiddleLimitE = makeApiDashboardElementSetter(setMiddleLimitG);
export const updateMiddleLimitE =
  makeApiDashboardElementSetter(updateMiddleLimitG);

export const getTailLimitE = makeApiDashboardElementGetter(getTailLimitG);
export const setTailLimitE = makeApiDashboardElementSetter(setTailLimitG);
export const updateTailLimitE = makeApiDashboardElementSetter(updateTailLimitG);

export const getDecimationE = makeApiDashboardElementGetter(getDecimationG);
export const setDecimationE = makeApiDashboardElementSetter(setDecimationG);
export const updateDecimationE =
  makeApiDashboardElementSetter(updateDecimationG);

export const getDataSeriesColumnsE = makeApiDashboardElementGetter(
  getDataSeriesColumnsG
);
export const getDataSeriesColumnAtE = makeApiDashboardElementGetter(
  getDataSeriesColumnAtG
);
export const findDataSeriesColumnE = makeApiDashboardElementGetter(
  findDataSeriesColumnG
);
export const setDataSeriesColumnsE = makeApiDashboardElementSetter(
  setDataSeriesColumnsG
);
export const setDataSeriesColumnAtE = makeApiDashboardElementSetter(
  setDataSeriesColumnAtG
);
export const updateDataSeriesColumnsE = makeApiDashboardElementSetter(
  updateDataSeriesColumnsG
);
export const updateDataSeriesColumnAtE = makeApiDashboardElementSetter(
  updateDataSeriesColumnAtG
);
export const updateEachDataSeriesColumnsE = makeApiDashboardElementSetter(
  updateEachDataSeriesColumnsG
);
export const insertDataSeriesColumnAtE = makeApiDashboardElementSetter(
  insertDataSeriesColumnAtG
);
export const removeDataSeriesColumnAtE = makeApiDashboardElementSetter(
  removeDataSeriesColumnAtG
);
export const removeDataSeriesColumnByDataSourceIndexE =
  makeApiDashboardElementSetter(removeDataSeriesColumnByDataSourceIndexG);

export const getAggregationTableColumnsE = makeApiDashboardElementGetter(
  getAggregationTableColumnsG
);
export const getAggregationTableColumnsByPredicateE =
  makeApiDashboardElementGetter(getAggregationTableColumnsByPredicateG);
export const getAggregationTableColumnsAtRowE = makeApiDashboardElementGetter(
  getAggregationTableColumnsAtRowG
);
export const getAggregationTableColumnAtEachRowE =
  makeApiDashboardElementGetter(getAggregationTableColumnAtEachRowG);
export const getAggregationTableColumnAtE = makeApiDashboardElementGetter(
  getAggregationTableColumnAtG
);
export const getFreeColumnAtE = makeApiDashboardElementGetter(getFreeColumnAtG);
export const setAggregationTableColumnsE = makeApiDashboardElementSetter(
  setAggregationTableColumnsG
);
export const setAggregationTableColumnsAtRowE = makeApiDashboardElementSetter(
  setAggregationTableColumnsAtRowG
);
export const setAggregationTableColumnAtEachRowE =
  makeApiDashboardElementSetter(setAggregationTableColumnAtEachRowG);
export const setAggregationTableColumnAtE = makeApiDashboardElementSetter(
  setAggregationTableColumnAtG
);
export const setFreeColumnAtE = makeApiDashboardElementSetter(setFreeColumnAtG);
export const updateAggregationTableColumnsE = makeApiDashboardElementSetter(
  updateAggregationTableColumnsG
);
export const updateAggregationTableColumnsAtRowE =
  makeApiDashboardElementSetter(updateAggregationTableColumnsAtRowG);
export const updateAggregationTableColumnAtE = makeApiDashboardElementSetter(
  updateAggregationTableColumnAtG
);
export const updateFreeColumnAtE =
  makeApiDashboardElementSetter(updateFreeColumnAtG);
export const updateFreeColumnAtEachRowE = makeApiDashboardElementSetter(
  updateFreeColumnAtEachRowG
);
export const updateAggregationTableColumnAtEachRowE =
  makeApiDashboardElementSetter(updateAggregationTableColumnAtEachRowG);
export const updateEachAggregationTableColumnE = makeApiDashboardElementSetter(
  updateEachAggregationTableColumnG
);
export const updateEachAggregationTableRowE = makeApiDashboardElementSetter(
  updateEachAggregationTableRowG
);
export const insertAggregationTableRowAtE = makeApiDashboardElementSetter(
  insertAggregationTableRowAtG
);
export const insertAggregationTableColumnAtEachRowE =
  makeApiDashboardElementSetter(insertAggregationTableColumnAtEachRowG);
export const insertFreeColumnAtEachRowE = makeApiDashboardElementSetter(
  insertFreeColumnAtEachRowG
);
export const leftAppendAggregationTableColumnAtEachRowE =
  makeApiDashboardElementSetter(leftAppendAggregationTableColumnAtEachRowG);
export const rightAppendAggregationTableColumnAtEachRowE =
  makeApiDashboardElementSetter(rightAppendAggregationTableColumnAtEachRowG);
export const removeAggregationTableRowAtE = makeApiDashboardElementSetter(
  removeAggregationTableRowAtG
);
export const removeAggregationTableColumnAtEachRowE =
  makeApiDashboardElementSetter(removeAggregationTableColumnAtEachRowG);
export const upAppendAggregationTableRowE = makeApiDashboardElementSetter(
  upAppendAggregationTableRowG
);
export const downAppendAggregationTableRowE = makeApiDashboardElementSetter(
  downAppendAggregationTableRowG
);

export const convertTableTypeE =
  makeApiDashboardElementSetter(convertTableType);

// -- Converters

export interface AdditionalColumn<T> {
  insert_index: number;
  name: string;
  value_by_row: Record<string, T>;
}

export function getAdditionalColumns(
  table: TableConfigured
): AdditionalColumn<string>[] {
  const config = table.properties.config;
  const intervals = table.properties.intervals;
  if (!isAggregatedTable(config)) {
    return [];
  }
  // Config has one AggregationRow per datasource; display expands each across intervals.
  return config.rows.reduce((outerAcc, row, rowIndex) => {
    const rowNameWithoutInterval =
      row.columns.find(isAggregatedColumn)?.column_name;
    if (!rowNameWithoutInterval) return outerAcc;
    const displayRowNames = intervals.map(
      (interval) => `${rowNameWithoutInterval} (${interval.name})`
    );
    return row.columns.reduce((InnerAcc, col, colIndex) => {
      if (!isFreeColumn(col)) {
        return InnerAcc;
      }
      const value_by_row = Object.fromEntries(
        displayRowNames.map((rowName) => [rowName, col.value])
      );
      if (rowIndex === 0) {
        return InnerAcc.concat({
          insert_index: colIndex,
          name: col.column_name,
          value_by_row,
        });
      }
      const additionalColumnIndex = InnerAcc.findIndex(
        (ac) => ac.name === col.column_name
      );
      if (additionalColumnIndex < 0) {
        return InnerAcc;
      }
      return [
        ...InnerAcc.slice(0, additionalColumnIndex),
        {
          ...InnerAcc[additionalColumnIndex]!,
          value_by_row: {
            ...InnerAcc[additionalColumnIndex]!.value_by_row,
            ...value_by_row,
          },
        },
        ...InnerAcc.slice(additionalColumnIndex + 1),
      ];
    }, outerAcc as AdditionalColumn<string>[]);
  }, [] as AdditionalColumn<string>[]);
}
